// Pure projections of a Torah "source journey" -- used by the UI to render a
// deterministic "How did I get here?" sentence, a "Summarize journey" object,
// an indented Journey LIST (the trail/map), and DOM-free side-path tuples.
//
// PURITY: this module is DOM-free and network-free. Every function is a pure
// projection over its arguments. The only "clock" input -- `now` in
// makeSidePath -- is passed in by the caller; we never call Date.now() here, so
// the functions stay deterministic and trivially testable.
//
// ACCURACY LOCK: every string these functions emit is assembled ONLY from the
// refs / labels / relationshipLabels the caller supplied. We never invent a
// relationship, never add a noun the caller did not pass (e.g. never append the
// word "verse" -- a node may be nested arbitrarily deep, see relationship.ts),
// and never fabricate a connection. The relationshipLabels are anchor-agnostic
// ("comments on this", not "comments on this verse") for exactly that reason.
//
// ARROW LOCK: the path arrow is the ASCII "->" everywhere, never a Unicode
// arrow character. (cp1252-safe, and consistent across the app.)

import type { RelStrength } from "./relationship.ts";

export interface PathNode {
  ref: string; // canonical ref, e.g. "Rashi on Numbers 15:38"
  label: string; // display label, e.g. "Rashi" or "Menachos 43b"
  labelHe: string | null; // Hebrew label if known, else null
  relationshipLabel: string; // anchor-agnostic, e.g. "comments on this" ("" for the root verse)
  strength: RelStrength | null; // null for the root verse node
  isVerse: boolean; // true only for the root verse node
}

export interface SidePath {
  id: string; // = path.join("|") -- deterministic, also the dedupe key
  parashaRef: string;
  path: string[]; // [verseRef, ...sourceRefs] -- the full path, the join key
  label: string; // human label, e.g. "Malbim - from Tehillim 33:6"
  savedAt: number;
}

export interface JourneyListItem {
  ref: string;
  label: string;
  relationshipLabel: string; // "" for anchor
  depth: number; // anchor=0, first hop=1, ...
  kind: "anchor" | "path" | "side";
  isCurrent?: boolean; // true only for the deepest path node (where you are now)
}

// "How did I get here?" -- ONE plain sentence built only from real refs +
// real relationship labels. Empty parenthetical when a node has no
// relationshipLabel. ASCII "->" between hops.
export function howDidIGetHere(anchorLabel: string, nodes: PathNode[]): string {
  if (nodes.length === 0) {
    return `From ${anchorLabel}.`;
  }
  const hops = nodes.map((node) => {
    const rel = node.relationshipLabel.trim();
    // Omit the parenthetical entirely when there is no relationship label --
    // never substitute an invented phrase.
    return rel ? `${node.label} (${rel})` : node.label;
  });
  return `From ${anchorLabel} -> ${hops.join(" -> ")}.`;
}

// "Summarize journey" -- a deterministic, purely-derived object. title is the
// anchor label; lines are the ordered trail starting with "Started at ...".
export function summarizeJourney(
  anchorLabel: string,
  nodes: PathNode[],
): { title: string; lines: string[] } {
  const lines: string[] = [`Started at ${anchorLabel}.`];
  nodes.forEach((node, i) => {
    const rel = node.relationshipLabel.trim();
    // Drop the "- <rel>" suffix when there is no relationship label.
    lines.push(rel ? `${i + 1}. ${node.label} - ${rel}` : `${i + 1}. ${node.label}`);
  });
  return { title: anchorLabel, lines };
}

// Build the indented Journey LIST: anchor (depth 0), then each hop as a "path"
// item (depth 1..n), then each saved side-path appended as a "side" item.
export function buildJourneyList(
  anchorLabel: string,
  nodes: PathNode[],
  sidePaths: SidePath[],
): JourneyListItem[] {
  const items: JourneyListItem[] = [
    {
      // No anchorRef is passed in this signature, so the anchor label doubles
      // as its ref -- we do not fabricate a ref.
      ref: anchorLabel,
      label: anchorLabel,
      relationshipLabel: "",
      depth: 0,
      kind: "anchor",
    },
  ];
  nodes.forEach((node, i) => {
    items.push({
      ref: node.ref,
      label: node.label,
      relationshipLabel: node.relationshipLabel,
      depth: i + 1,
      kind: "path",
      isCurrent: i === nodes.length - 1, // the deepest hop is "where you are now"
    });
  });
  for (const side of sidePaths) {
    items.push({
      // The side item points at the LAST ref of its saved path -- where that
      // branch lands.
      ref: side.path[side.path.length - 1],
      label: side.label,
      relationshipLabel: "",
      depth: 1,
      kind: "side",
    });
  }
  return items;
}

// Construct a SidePath tuple. id is the deterministic path join; savedAt is the
// caller-supplied `now` (never Date.now() here).
export function makeSidePath(
  parashaRef: string,
  path: string[],
  label: string,
  now: number,
): SidePath {
  return {
    id: path.join("|"),
    parashaRef,
    path,
    label,
    savedAt: now,
  };
}

// A crumb in the compact "You are here" path strip. `kind === "gap"` is the
// truncation marker ("...") for long journeys; it carries no ref and is inert.
export interface PathCrumb {
  label: string;
  ref: string; // "" for the anchor (we do not fabricate one) and for the gap
  index: number; // -1 for the gap; 0 = anchor; 1..n = hop into nodes[index-1]
  kind: "anchor" | "hop" | "gap";
  isCurrent: boolean;
}

// Build the compact path strip [anchor -> ... -> last two hops], truncating the
// MIDDLE while always preserving the start and the current node. `maxCrumbs` is
// the most real (non-gap) crumbs to show before truncating (default 4). Pure
// projection over the supplied labels - never invents a label or a ref.
export function compactPath(
  anchorLabel: string,
  nodes: PathNode[],
  maxCrumbs = 4,
): PathCrumb[] {
  const all: PathCrumb[] = [
    { label: anchorLabel, ref: "", index: 0, kind: "anchor", isCurrent: nodes.length === 0 },
  ];
  nodes.forEach((n, i) => {
    all.push({
      label: n.label,
      ref: n.ref,
      index: i + 1,
      kind: "hop",
      isCurrent: i === nodes.length - 1,
    });
  });
  if (all.length <= maxCrumbs) return all;
  // Keep the anchor + the last (maxCrumbs - 1) crumbs, with a gap between.
  const tailCount = Math.max(1, maxCrumbs - 1);
  const head = all[0];
  const tail = all.slice(all.length - tailCount);
  return [head, { label: "...", ref: "", index: -1, kind: "gap", isCurrent: false }, ...tail];
}

// Keep the FIRST occurrence of each id, drop later duplicates, preserve order.
export function dedupeSidePaths(list: SidePath[]): SidePath[] {
  const seen = new Set<string>();
  const out: SidePath[] = [];
  for (const sp of list) {
    if (seen.has(sp.id)) continue;
    seen.add(sp.id);
    out.push(sp);
  }
  return out;
}
